/**
 * Phase-5 HQ furnishing inventory.
 *
 * Placements are data, grouped under the authoritative Phase-4 zone roots.
 * Every GLB clone shares the cached container's meshes/materials; every zone
 * also receives its own dynamic batch group so batching and culling never span
 * an entire floor while quantized nested GLBs remain visible on Chrome/Metal.
 */
import * as pc from 'playcanvas'
import { getPropEntry } from '../assets'
import type { AssetZoneController } from '../assetZones'
import { spawnProp } from './dressing'
import {
  FURNITURE_ASSET_BOUNDS,
  furnitureDimensions,
  furnitureFootprint,
  furnitureFootprintsOverlap,
  type FurnitureAssetBounds,
  type FurnitureDimensions,
  type FurnitureFootprint,
} from './furnitureGeometry'
import type { HqProgramZone } from './floorPrograms'

export interface HqFurniturePlacement {
  id: string
  zoneId: string
  file: string
  x: number
  y: number
  z: number
  rotY: number
  scale: number
  /** Non-blocking surface layer or object mounted on another furnishing. */
  touch?: true
  /** Composite seating set whose members may tuck under one another. */
  setId?: string
  /** Built-in room module authored alongside furniture but not human-scale furnishing. */
  role?: 'architecture'
}

export type HqFurnitureAssetBounds = FurnitureAssetBounds
export type HqFurnitureFootprint = FurnitureFootprint
export type HqFurnitureDimensions = FurnitureDimensions
export const HQ_FURNITURE_ASSET_BOUNDS = FURNITURE_ASSET_BOUNDS
export const hqFurnitureDimensions = furnitureDimensions
export const hqFurnitureFootprint = furnitureFootprint

function item(
  zoneId: string,
  id: string,
  file: string,
  x: number,
  z: number,
  rotY = 0,
  scale = 1,
  y = 0,
  touch?: true,
  setId?: string,
): HqFurniturePlacement {
  const floor = Number(zoneId[1])
  return { id, zoneId, file, x, y: floor * 4.5 + y, z, rotY, scale, touch, setId }
}

function architecture(placement: HqFurniturePlacement): HqFurniturePlacement {
  return { ...placement, role: 'architecture' }
}

export const HQ_FURNITURE_SHADOW_KEYS = new Set([
  'l2-boardroom:conference-table',
  'l2-bay-lounge:primary-table',
  'l3-penthouse-lounge:grand-couch-second',
  'l3-penthouse-lounge:primary-table',
  'l3-sky-bar:bar-counter-middle',
  'l3-executive-nook:exec-desk',
])
export const HQ_FURNITURE_BATCH_MAX_AABB = 14

/**
 * A deliberately spacious four-seat conversation setting. The previous
 * version tucked all four pieces into a roughly 3 x 3 m knot and then exempted
 * the resulting intersections with setId. This arrangement is disjoint even
 * without that escape hatch, so its circulation can be audited honestly.
 */
function bayConversationSet(
  zoneId: 'l0-bay-lounge' | 'l2-bay-lounge' | 'l3-penthouse-lounge',
  cz = 0,
): HqFurniturePlacement[] {
  return [
    item(zoneId, 'primary-rug', 'synty_office_rug_01.glb', 21.1, cz, 90, 1.05, 0.01, true),
    item(zoneId, 'primary-couch', 'synty_office_couch_03.glb', 22.75, cz, 270),
    item(zoneId, 'primary-chair-n', 'kf_armchair.glb', 20.05, cz - 2.15, 35, 0.65),
    item(zoneId, 'primary-chair-s', 'kf_armchair.glb', 20.05, cz + 2.15, 145, 0.65),
    item(zoneId, 'primary-table', 'synty_office_coffee_table_01.glb', 20.8, cz, 90),
    item(zoneId, 'primary-lamp', 'kf_lamp_standing.glb', 23.25, cz - 3.2, 0, 0.72),
    item(zoneId, 'primary-plant', 'synty_office_plant_08.glb', 23.15, cz + 3.2, 0, 2.4),
  ]
}

/** L1 is a low-stimulation focus/view lounge, not another social sofa group. */
function bayFocusSet(): HqFurniturePlacement[] {
  const zoneId = 'l1-bay-lounge'
  return [
    item(zoneId, 'focus-rug', 'kf_rug_rectangle_A.glb', 21, 1.5, 90, 0.9, 0.01, true),
    item(zoneId, 'focus-chair-n', 'kf_armchair_pillows.glb', 21, 0.1, 0, 0.7),
    item(zoneId, 'focus-chair-s', 'kf_armchair.glb', 21, 2.9, 180, 0.7),
    item(zoneId, 'focus-table', 'kf_table_small.glb', 21, 1.5, 0, 0.85),
    item(zoneId, 'focus-lamp', 'kf_lamp_standing.glb', 23.1, 3.8, 0, 0.75),
    item(zoneId, 'focus-plant', 'prop_pottedPlant.glb', 19.1, 3.8, 0, 1.3),
  ]
}

export const HQ_FURNITURE: readonly HqFurniturePlacement[] = [
  // L0 static furnishing is authoritative here. Behavior-owned server and
  // behavior-owned server entities keep their lifecycle, but attach here.
  // Reception occupies the west side; the full 2.4 m entry axis stays open.
  item('l0-reception', 'primary-counter', 'kf_cabinet_medium.glb', -5.7, 12.7, 0),
  item('l0-reception', 'primary-counter-east', 'kf_cabinet_medium.glb', -3.7, 12.7, 0),
  item('l0-reception', 'reception-chair', 'kf_chair_A.glb', -4.7, 14.35, 180, 0.8),
  item('l0-reception', 'visitor-chair', 'kf_armchair.glb', 4.7, 12.8, 270, 0.75),
  item('l0-reception', 'visitor-side-table', 'kf_table_small.glb', 3.25, 12.8, 0, 0.65),
  item('l0-reception', 'visitor-laptop', 'prop_laptop.glb', -4.7, 12.7, 180, 1, 1.02, true),
  item('l0-reception', 'reception-papers', 'kf_book_set.glb', -3.8, 12.7, 20, 0.45, 1.12, true),
  item('l0-reception', 'door-planter-west', 'kf_cactus_medium_A.glb', -2.4, 14.2, 0, 0.85),
  item('l0-reception', 'door-planter-east', 'kf_cactus_medium_A.glb', 2.4, 14.2, 0, 0.85),
  item('l0-server-room', 'rack-extra-west-a', 'prop_serverComputerLarge.glb', -23.1, -12.8, 90, 0.9),
  item('l0-server-room', 'rack-extra-west-b', 'prop_serverComputerLarge.glb', -23.1, -11.2, 90, 0.9),
  item('l0-server-room', 'rack-extra-east-a', 'prop_serverComputerLarge.glb', -18.9, -13.2, 270, 0.9),
  item('l0-server-room', 'rack-extra-east-b', 'prop_serverComputerLarge.glb', -18.9, -11.8, 270, 0.9),
  ...bayConversationSet('l0-bay-lounge'),
  item('l0-bay-lounge', 'wall-shelf-n', 'kf_shelf_A_big.glb', 17.6, -5.9, 90, 1, 1.2),
  item('l0-bay-lounge', 'wall-shelf-s', 'kf_shelf_B_large.glb', 17.6, 5.9, 90, 1, 1.2),

  // L1 focus lounge and gallery viewing furniture.
  ...bayFocusSet(),
  item('l1-bay-lounge', 'focus-stool-a', 'kf_chair_stool.glb', 21.5, -4.6, 270, 0.85),
  item('l1-bay-lounge', 'focus-stool-b', 'kf_chair_stool.glb', 21.5, -3.5, 270, 0.85),
  item('l1-bay-lounge', 'bay-bar-shelf', 'kf_cabinet_medium.glb', 23.25, -4.05, 270, 0.8),
  item('l1-bay-lounge', 'view-laptop', 'prop_laptop.glb', 23.05, -4.05, 90, 0.9, 0.82, true),
  item('l1-gallery', 'gallery-bench-a', 'kf_table_medium_long.glb', -18, -14.05, 0, 0.4),
  item('l1-gallery', 'gallery-bench-b', 'kf_table_medium_long.glb', -10, -14.05, 0, 0.4),

  // L1 west-bay focus studio: two properly scaled Synty work settings against
  // the west wall. Both chairs face their desks and the centred east doorway
  // keeps a direct 1.2 m approach from the production neighborhood.
  architecture(item('l1-focus-studio', 'synty-glass-front', 'synty_office_glass_door_large_01.glb', -18.18, -0.625, 90)),
  item('l1-focus-studio', 'focus-desk-north', 'synty_office_desk_01.glb', -22.7, -4.45, 90),
  item('l1-focus-studio', 'focus-chair-north', 'synty_office_chair_01.glb', -21.25, -4.45, 270),
  item('l1-focus-studio', 'focus-monitor-north', 'synty_office_monitor_double_01.glb', -22.7, -4.45, 90, 0.8, 0.75, true),
  item('l1-focus-studio', 'focus-desk-south', 'synty_office_desk_01.glb', -22.7, -1.45, 90),
  item('l1-focus-studio', 'focus-chair-south', 'synty_office_chair_01.glb', -21.25, -1.45, 270),
  item('l1-focus-studio', 'focus-monitor-south', 'synty_office_monitor_double_01.glb', -22.7, -1.45, 90, 0.8, 0.75, true),
  item('l1-focus-studio', 'focus-plant', 'synty_office_plant_08.glb', -18.75, -5.25, 0, 2.2),
  item('l1-focus-studio', 'focus-divider', 'synty_office_divider_half_01.glb', -23.3, -2.55, 90, 0.75),
  item('l1-focus-studio', 'focus-whiteboard', 'synty_office_whiteboard_01.glb', -23.9, -3, 90, 1, 0.95, true),
  item('l1-focus-studio', 'focus-lamp-north', 'synty_office_desk_lamp_01.glb', -22.75, -4.95, 90, 0.8, 0.75, true),
  item('l1-focus-studio', 'focus-lamp-south', 'synty_office_desk_lamp_01.glb', -22.75, -1.95, 90, 0.8, 0.75, true),

  // L1 team huddle: a compact four-person hybrid room rather than another
  // oversized board table. Synty chairs unify it with the adjacent studio and
  // the east-side Synty doorway remains a straight approach.
  architecture(item('l1-team-huddle', 'synty-glass-front', 'synty_office_glass_door_large_01.glb', -18.18, 5.625, 90)),
  item('l1-team-huddle', 'huddle-table', 'synty_office_table_round_01.glb', -21.7, 3.1),
  item('l1-team-huddle', 'huddle-chair-nw', 'synty_office_chair_01.glb', -22.9, 3.1, 90),
  item('l1-team-huddle', 'huddle-chair-sw', 'synty_office_chair_01.glb', -21.7, 4.3, 180),
  item('l1-team-huddle', 'huddle-chair-ne', 'synty_office_chair_01.glb', -20.5, 3.1, 270),
  item('l1-team-huddle', 'huddle-chair-se', 'synty_office_chair_01.glb', -21.7, 1.9, 0),
  item('l1-team-huddle', 'huddle-laptop', 'prop_laptop.glb', -21.7, 3.1, 90, 0.75, 0.76, true),
  item('l1-team-huddle', 'huddle-credenza', 'synty_office_shelf_01.glb', -23.7, 5, 90),
  item('l1-team-huddle', 'huddle-whiteboard', 'synty_office_whiteboard_01.glb', -23.9, 4.2, 90, 1, 0.95, true),
  item('l1-team-huddle', 'huddle-plant', 'synty_office_plant_08.glb', -19.05, 5.2, 0, 2.2),

  // L2 boardroom: one credible conference table, ten facing seats, a media
  // wall, and a compact east credenza. The west door approach remains empty.
  item('l2-boardroom', 'conference-table', 'synty_office_table_conference_01.glb', 9.5, -9),
  ...Array.from({ length: 4 }, (_, index) =>
    item('l2-boardroom', `chair-north-${index}`, 'synty_office_chair_01.glb', 7.5 + index * 1.35, -10.8, 0)),
  ...Array.from({ length: 4 }, (_, index) =>
    item('l2-boardroom', `chair-south-${index}`, 'synty_office_chair_01.glb', 7.5 + index * 1.35, -7.2, 180)),
  item('l2-boardroom', 'chair-west-head', 'synty_office_chair_01.glb', 6.8, -9, 90),
  item('l2-boardroom', 'chair-east-head', 'synty_office_chair_01.glb', 12.2, -9, 270),
  item('l2-boardroom', 'media-screen', 'synty_office_projector_screen_01.glb', 9.5, -11.85, 0, 1, 0.85, true),
  item('l2-boardroom', 'credenza', 'synty_office_shelf_01.glb', 15.8, -9, 90),
  item('l2-boardroom', 'plant-a', 'kf_cactus_medium_A.glb', 3.2, -11.2),
  item('l2-boardroom', 'plant-b', 'kf_cactus_medium_A.glb', 16, -11.2),
  item('l2-boardroom', 'table-books', 'kf_book_set.glb', 9, -9, 20, 1, 1.05, true),
  item('l2-boardroom', 'table-laptop', 'prop_laptop.glb', 10, -9, 90, 0.9, 0.82, true),

  // L2 kitchen/break: a readable appliance/counter run plus one four-seat
  // coffee table. Reducing the former 17-piece knot restores the west egress.
  item('l2-kitchen', 'counter-a', 'synty_office_kitchen_counter_01.glb', 18, 13.85, 180),
  item('l2-kitchen', 'counter-b', 'synty_office_kitchen_counter_01.glb', 19, 13.85, 180),
  item('l2-kitchen', 'counter-sink', 'synty_office_kitchen_counter_sink_01.glb', 20, 13.85, 180),
  item('l2-kitchen', 'counter-c', 'synty_office_kitchen_counter_01.glb', 21, 13.85, 180),
  item('l2-kitchen', 'fridge', 'synty_office_fridge_01.glb', 22.9, 13.35, 180),
  item('l2-kitchen', 'coffee', 'synty_office_coffee_machine_01.glb', 18.5, 13.25, 180, 0.5, 0.95, true),
  item('l2-kitchen', 'coffee-table', 'synty_office_table_round_01.glb', 21.3, 10.2),
  item('l2-kitchen', 'coffee-chair-west', 'synty_office_chair_01.glb', 20.1, 10.2, 90),
  item('l2-kitchen', 'coffee-chair-east', 'synty_office_chair_01.glb', 22.5, 10.2, 270),
  item('l2-kitchen', 'coffee-chair-north', 'synty_office_chair_01.glb', 21.3, 9, 0),
  item('l2-kitchen', 'coffee-chair-south', 'synty_office_chair_01.glb', 21.3, 11.4, 180),
  item('l2-kitchen', 'pantry-shelf', 'synty_office_shelf_01.glb', 23.55, 8.25, 270),
  item('l2-kitchen', 'break-plant', 'synty_office_plant_08.glb', 18.1, 8.1, 0, 2.5),
  item('l2-gallery', 'mezzanine-bench-a', 'kf_table_medium_long.glb', -3.75, 2.1, 90, 0.3),
  item('l2-gallery', 'mezzanine-bench-b', 'kf_table_medium_long.glb', -3.75, 4.4, 90, 0.3),
  ...bayConversationSet('l2-bay-lounge'),

  // L3 premium lounge, sky bar, executive nook, and Core shrine.
  ...bayConversationSet('l3-penthouse-lounge', -0.8),
  item('l3-penthouse-lounge', 'grand-couch-second', 'synty_office_couch_03.glb', 21.4, 4.8, 180),
  item('l3-penthouse-lounge', 'grand-lamp-second', 'kf_lamp_standing.glb', 17.8, 5.4, 0, 0.75),
  item('l3-penthouse-lounge', 'grand-side-a', 'kf_table_small.glb', 19.2, 5.3, 0, 0.8),
  item('l3-sky-bar', 'bar-counter-west', 'kf_cabinet_medium.glb', 2.5, -11.4, 0, 1, 0, undefined, 'bar-counter'),
  item('l3-sky-bar', 'bar-counter-middle', 'kf_cabinet_medium.glb', 4.5, -11.4, 0, 1, 0, undefined, 'bar-counter'),
  item('l3-sky-bar', 'bar-counter-east', 'kf_cabinet_medium.glb', 6.5, -11.4, 0, 1, 0, undefined, 'bar-counter'),
  item('l3-sky-bar', 'back-bar', 'kf_shelf_B_large.glb', 4.5, -13.2, 180, 1.2, 1.2),
  ...Array.from({ length: 6 }, (_, index) =>
    item('l3-sky-bar', `bar-stool-${index}`, 'kf_chair_stool.glb', 2.2 + index * 0.9, -9.2, 180, 1.2)),
  item('l3-sky-bar', 'cafe-table-a', 'kf_table_small.glb', 1.0, -12, 0, 0.75),
  item('l3-sky-bar', 'cafe-table-b', 'kf_table_small.glb', 1.0, -13, 0, 0.75),
  item('l3-sky-bar', 'espresso', 'prop_kitchenCoffeeMachine.glb', 6.2, -13.2, 180, 1, 1.58, true),
  item('l3-executive-nook', 'exec-desk', 'synty_office_desk_03.glb', -16.5, -10.5, 90),
  item('l3-executive-nook', 'exec-chair', 'synty_office_chair_01.glb', -15.25, -10.5, 270),
  item('l3-executive-nook', 'exec-lamp', 'synty_office_desk_lamp_01.glb', -16.5, -10.95, 90, 0.9, 0.75, true),
  item('l3-executive-nook', 'guest-couch', 'synty_office_couch_03.glb', -12.65, -11.8, 90),
  item('l3-executive-nook', 'guest-table', 'synty_office_coffee_table_01.glb', -14.15, -11.8, 90),
  item('l3-executive-nook', 'timeline', 'synty_office_shelf_01.glb', -15, -13.6, 0),
  item('l3-executive-nook', 'strategy-board', 'synty_office_whiteboard_01.glb', -17.9, -12.4, 90, 1, 0.95, true),
  item('l3-executive-nook', 'exec-plant', 'synty_office_plant_08.glb', -17.4, -8.6, 0, 2.5),
  // Leave the supported overlook approach (X 2.5..5.5, Z 6.12..8.4) open.
  item('l3-core-crown', 'contemplation-bench-a', 'kf_table_medium_long.glb', 1.7, 7.5, 90, 0.6),
  item('l3-core-crown', 'contemplation-bench-b', 'kf_table_medium_long.glb', 6, 10.3, 200, 0.5),
  item('l3-core-crown', 'shrine-planter-a', 'kf_cactus_medium_A.glb', 1.2, 10.5),
  item('l3-core-crown', 'shrine-planter-b', 'kf_cactus_medium_A.glb', 6.8, 7.2),
]

/** Minimum clear approach: a 1.2 m-wide path carried 1.2 m into each zone. */
export const HQ_FURNITURE_ACCESS_WIDTH = 1.2
export const HQ_FURNITURE_ACCESS_DEPTH = 1.2

/**
 * The HQ's real double-door axis, carried from the spawn point through the
 * entire reception zone. It is intentionally wider than the generic zone
 * approach because it is both arrival sequence and exit discharge.
 */
export const HQ_ENTRY_EGRESS_FOOTPRINT: HqFurnitureFootprint = {
  minX: -1.2,
  maxX: 1.2,
  minZ: 9.5,
  maxZ: 15,
}

export type HqFurnitureLayoutIssueKind =
  | 'missing-asset-bounds'
  | 'outside-zone'
  | 'overlap'
  | 'blocked-access'
  | 'blocked-egress'

export interface HqFurnitureLayoutIssue {
  kind: HqFurnitureLayoutIssueKind
  zoneId: string
  itemIds: readonly string[]
}

function overlaps(a: HqFurnitureFootprint, b: HqFurnitureFootprint): boolean {
  return furnitureFootprintsOverlap(a, b)
}

/** Axis-aligned approach corridor from the declared access point into a zone. */
export function hqZoneAccessFootprint(zone: HqProgramZone): HqFurnitureFootprint {
  const [x, z] = zone.access
  const halfWidth = HQ_FURNITURE_ACCESS_WIDTH / 2
  const distances = [
    { edge: 'west', distance: Math.abs(x - zone.bounds.minX) },
    { edge: 'east', distance: Math.abs(x - zone.bounds.maxX) },
    { edge: 'north', distance: Math.abs(z - zone.bounds.minZ) },
    { edge: 'south', distance: Math.abs(z - zone.bounds.maxZ) },
  ] as const
  const edge = [...distances].sort((a, b) => a.distance - b.distance)[0].edge
  if (edge === 'west') {
    return {
      minX: Math.min(x, zone.bounds.minX),
      maxX: zone.bounds.minX + HQ_FURNITURE_ACCESS_DEPTH,
      minZ: z - halfWidth,
      maxZ: z + halfWidth,
    }
  }
  if (edge === 'east') {
    return {
      minX: zone.bounds.maxX - HQ_FURNITURE_ACCESS_DEPTH,
      maxX: Math.max(x, zone.bounds.maxX),
      minZ: z - halfWidth,
      maxZ: z + halfWidth,
    }
  }
  if (edge === 'north') {
    return {
      minX: x - halfWidth,
      maxX: x + halfWidth,
      minZ: Math.min(z, zone.bounds.minZ),
      maxZ: zone.bounds.minZ + HQ_FURNITURE_ACCESS_DEPTH,
    }
  }
  return {
    minX: x - halfWidth,
    maxX: x + halfWidth,
    minZ: zone.bounds.maxZ - HQ_FURNITURE_ACCESS_DEPTH,
    maxZ: Math.max(z, zone.bounds.maxZ),
  }
}

/**
 * Static Phase-5 layout audit. Non-blocking surface layers and mounted touches
 * are checked for containment but excluded from ground overlap/access checks.
 */
export function validateHqFurnitureLayout(
  zones: readonly HqProgramZone[],
  placements: readonly HqFurniturePlacement[] = HQ_FURNITURE,
): HqFurnitureLayoutIssue[] {
  const zoneById = new Map(zones.map((zone) => [zone.id, zone]))
  const issues: HqFurnitureLayoutIssue[] = []
  const footprints = new Map<HqFurniturePlacement, HqFurnitureFootprint>()

  for (const placement of placements) {
    const footprint = hqFurnitureFootprint(placement)
    if (!footprint) {
      issues.push({ kind: 'missing-asset-bounds', zoneId: placement.zoneId, itemIds: [placement.id] })
      continue
    }
    footprints.set(placement, footprint)
    const zone = zoneById.get(placement.zoneId)
    if (!zone) continue
    if (
      footprint.minX < zone.bounds.minX
      || footprint.maxX > zone.bounds.maxX
      || footprint.minZ < zone.bounds.minZ
      || footprint.maxZ > zone.bounds.maxZ
    ) {
      issues.push({ kind: 'outside-zone', zoneId: placement.zoneId, itemIds: [placement.id] })
    }
  }

  for (const zone of zones) {
    const floorItems = placements.filter((placement) =>
      placement.zoneId === zone.id && !placement.touch && footprints.has(placement))
    const access = hqZoneAccessFootprint(zone)
    for (const placement of floorItems) {
      // A doorway module's outer AABB spans both glass leaves and the open
      // centre. Circulation is enforced by the authoritative split colliders,
      // so treating that hollow architectural AABB as furniture is a false hit.
      if (placement.role !== 'architecture' && overlaps(footprints.get(placement)!, access)) {
        issues.push({ kind: 'blocked-access', zoneId: zone.id, itemIds: [placement.id] })
      }
      if (
        zone.id === 'l0-reception'
        && overlaps(footprints.get(placement)!, HQ_ENTRY_EGRESS_FOOTPRINT)
      ) {
        issues.push({ kind: 'blocked-egress', zoneId: zone.id, itemIds: [placement.id] })
      }
    }
    for (let index = 0; index < floorItems.length; index++) {
      for (let otherIndex = index + 1; otherIndex < floorItems.length; otherIndex++) {
        const first = floorItems[index]
        const second = floorItems[otherIndex]
        if (first.setId !== undefined && first.setId === second.setId) continue
        if (overlaps(footprints.get(first)!, footprints.get(second)!)) {
          issues.push({ kind: 'overlap', zoneId: zone.id, itemIds: [first.id, second.id] })
        }
      }
    }
  }
  return issues
}

export interface HqFurnishingHandles {
  zoneRoots: ReadonlyMap<string, pc.Entity>
  entities: readonly pc.Entity[]
  batchGroupIds: ReadonlyMap<string, number>
}

/** Runtime-catalog assets that are intentionally requested by this HQ layout. */
export function hqOnDemandAssetFiles(
  placements: readonly HqFurniturePlacement[] = HQ_FURNITURE,
): string[] {
  return [...new Set(placements
    .filter((placement) => {
      const loadClass = getPropEntry(placement.file)?.runtime?.loadClass
      return loadClass === 'zone' || loadClass === 'deferred'
    })
    .map((placement) => placement.file))].sort()
}

function createZoneBatchGroups(
  app: pc.Application,
  zoneRoots: ReadonlyMap<string, pc.Entity>,
): ReadonlyMap<string, number> {
  const batcher = (app as pc.Application & {
    batcher?: Pick<pc.BatchManager, 'addGroup'>
  }).batcher
  if (!batcher) return new Map()
  const furnishedZones = new Set(HQ_FURNITURE.map((item) => item.zoneId))
  return new Map([...zoneRoots.keys()]
    .filter((zoneId) => furnishedZones.has(zoneId))
    .map((zoneId) => {
      const group = batcher.addGroup(
        `hq-furniture:${zoneId}`,
        // Static batching submits these nested quantized GLBs but fails to
        // rasterize them on the production Chrome/Metal path. Dynamic groups
        // preserve visible source transforms while retaining per-zone draws.
        true,
        HQ_FURNITURE_BATCH_MAX_AABB,
      )
      return [zoneId, group.id] as const
    }))
}

export function buildHqFurnishing(
  app: pc.Application,
  zoneRoots: ReadonlyMap<string, pc.Entity>,
  assetZones?: AssetZoneController<pc.Asset>,
): HqFurnishingHandles {
  const batchGroupIds = createZoneBatchGroups(app, zoneRoots)
  const entities = HQ_FURNITURE.map((placement) => {
    const parent = zoneRoots.get(placement.zoneId)
    if (!parent) throw new Error(`missing HQ furniture zone ${placement.zoneId}`)
    const runtime = getPropEntry(placement.file)?.runtime
    const entity = spawnProp(
      app,
      placement.file,
      [placement.x, placement.y, placement.z],
      placement.rotY,
      placement.scale,
      parent,
      {
        batchGroupId: batchGroupIds.get(placement.zoneId),
        castShadows: HQ_FURNITURE_SHADOW_KEYS.has(
          `${placement.zoneId}:${placement.id}`,
        ),
        assetZones: runtime?.loadClass === 'zone' ? assetZones : undefined,
        assetZone: runtime?.loadClass === 'zone' ? runtime.zone : undefined,
      },
    )
    entity.name = `hq-furniture-${placement.zoneId}-${placement.id}`
    return entity
  })
  return { zoneRoots, entities, batchGroupIds }
}
